// OWNER: LG-ADMIN. Column widths for the two dense Lead Search tables (Searches, Results). Same approach as the admin
// pages (web/src/admin/layout.ts): a <colgroup> from these specs with `table-layout: fixed`, so the widths here are the
// rendered widths and a table can't push columns off-screen at 1280×720. tests/lg-admin-logic.test.ts checks the budget.
import { CELL_PAD_X, type ColumnSpec } from '../admin/layout';

/** actions fits "Raise budget" as one whole button; spend stacks the spent amount over the budget. */
export type SearchColumnKey = 'id' | 'niche' | 'areas' | 'status' | 'progress' | 'spend' | 'failed' | 'actions';
export const SEARCH_COLUMNS: ReadonlyArray<ColumnSpec<SearchColumnKey>> = [
  { key: 'id', label: '#', width: 48, num: true },
  { key: 'niche', label: 'Niche · mode', width: 158 },
  { key: 'areas', label: 'Areas', width: 150 },
  { key: 'status', label: 'Status', width: 118 },
  { key: 'progress', label: 'Progress', width: 224, title: 'Discovered, gated, enriched, classified, scored: finished tasks of all tasks.' },
  { key: 'spend', label: 'Spent / budget', width: 104, num: true, title: 'Spent or reserved (whole cents; hover for the exact amount), with the budget below.' },
  { key: 'failed', label: 'Failed tasks', width: 70, num: true },
  { key: 'actions', label: '', srLabel: 'Actions', width: 140 },
];

export type ResultColumnKey = 'select' | 'tier' | 'name' | 'where' | 'reasons' | 'hook' | 'flags' | 'actions';
/** flags fits the longest word of any flag label as a chip ("provisional", "temporarily"); actions fits the phone on
 *  one line and each review button whole (they wrap as whole buttons, never mid-word). */
export const RESULT_COLUMNS: ReadonlyArray<ColumnSpec<ResultColumnKey>> = [
  { key: 'select', label: '', srLabel: 'Select', width: 34 },
  { key: 'tier', label: 'Tier', width: 58, title: 'Tier and priority (0-100) for the offer this search sorts by.' },
  { key: 'name', label: 'Business', width: 170 },
  { key: 'where', label: 'Where', width: 96 },
  { key: 'reasons', label: 'Top reasons', width: 200 },
  { key: 'hook', label: 'Hook', width: 200, title: 'The primary hook for this offer, with its usage and evidence.' },
  { key: 'flags', label: 'Flags', width: 118 },
  { key: 'actions', label: '', srLabel: 'Links and review', width: 138 },
];

// ---------- text width estimates for what sits inside those cells (checked in the browser at 1280x720) ----------

/** `.chip`: 12 px IBM Plex Mono (0.6 em a character) with 8 px padding each side. */
export const CHIP_CHAR_PX = 7.2;
export const CHIP_PAD_X = 8;
/** `.btn-sm`: 13 px Figtree 600, 10 px padding and a 1 px border each side. 8.1 px a character is an upper bound. */
export const BTN_SM_CHAR_PX = 8.1;
export const BTN_SM_EDGE_PX = 11;
/** Room inside a fixed-table cell (admin.css pads 8 px each side). */
export const cellContent = (width: number): number => width - 2 * CELL_PAD_X;
/** The narrowest a chip can get when it may wrap only at spaces: its longest word. */
export const chipMinWidth = (label: string): number => Math.ceil(Math.max(0, ...label.split(/\s+/).map((w) => w.length)) * CHIP_CHAR_PX) + 2 * CHIP_PAD_X;
/** A small button never wraps (white-space: nowrap), so it needs its whole label. */
export const btnSmWidth = (label: string): number => Math.ceil(label.length * BTN_SM_CHAR_PX) + 2 * BTN_SM_EDGE_PX;
/** Mono text on one line (the phone number). */
export const monoWidth = (text: string): number => Math.ceil(text.length * CHIP_CHAR_PX);
